import PropTypes from "prop-types";
import React, { useEffect, useReducer, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { useHistory, useLocation } from "react-router-dom";
import CityDropdowns from "../components/CityDropdowns";
import { RecruitmentRow1 } from "../components/DataRow";
import Navbar from "../components/Navbar";
import PlayImg from "../components/PlayImg";
import { useScrollRestoration } from "../hooks/useScrollRestoration";
import reducer from "../reducer";

const STORAGE_KEY = "recruitmentListState";

function Salary({ onChange, value }) {
  return (
    <input
      type="number"
      className="form-control"
      style={{ fontSize: 14 }}
      placeholder="薪资"
      defaultValue={value}
      onBlur={onChange}
    />
  );
}

Salary.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
};

Salary.defaultProps = {
  value: "",
};

function EducationPicker({ onChange, value }) {
  return (
    <select
      className="form-control"
      style={{ fontSize: 14 }}
      onChange={onChange}
      defaultValue={value}
    >
      <option value="">学历要求</option>
      <option>高中及以上</option>
      <option>大专及以上</option>
      <option>本科及以上</option>
      <option>硕士及以上</option>
      <option>博士</option>
    </select>
  );
}

EducationPicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
};

EducationPicker.defaultProps = {
  value: "",
};

function IndustryPicker({ onChange, value }) {
  const [industry, setIndustry] = React.useState([]);
  const [selected, setSelected] = React.useState(value);

  useEffect(() => {
    const fetchData = async () => {
      const uri = [
        "/crate-api-data",
        "/mysql",
        "/ovaphlow.setting?",
        "f=equal,4,category,行业,ref_id,0",
      ];
      const response = await fetch(uri.join(""));
      if (response.status < 400) {
        const data = await response.json();
        const d = data.map((item) => ({ id: item.id, name: JSON.parse(item.detail)?.name }));
        setIndustry(d);
        sessionStorage.setItem("industry", JSON.stringify(d));
      }
    };

    const sd = sessionStorage.getItem("industry");
    if (sd) {
      setIndustry(JSON.parse(sd));
    } else {
      fetchData();
    }
  }, []);

  const handleChange = (e) => {
    setSelected(e.target.value);
    onChange(e);
  };

  return (
    <select
      className="form-control"
      style={{ fontSize: 14 }}
      onChange={handleChange}
      value={selected}
    >
      <option value="">行业</option>
      {industry.map((item) => (
        <option key={item.id} value={item.name}>
          {item.name}
        </option>
      ))}
    </select>
  );
}

IndustryPicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
};

IndustryPicker.defaultProps = {
  value: "",
};

async function getJobList({ city, industry, education, salary, category, page = 1 }) {
  const take = 100;
  const filter = [];

  filter.push("eq", 2, "status", "在招");

  const equal = [];
  if (city) {
    equal.push("address2", city);
  }
  if (equal.length > 0) {
    filter.push("equal", equal.length, ...equal);
  }

  const like = [];
  if (industry) {
    like.push("industry", industry);
  }
  if (education) {
    like.push("education", education);
  }
  if (category) {
    like.push("category", category);
  }
  if (like.length > 0) {
    filter.push("like", like.length, ...like);
  }

  if (salary && salary > 0) {
    filter.push("less-equal", 2, "salary1", salary);
    filter.push("greater-equal", 2, "salary2", salary);
  }

  const uri = [
    "/crate-api-data",
    "/mysql",
    "/billboard.recruitment?",
    `f=${filter}`,
    `&l=order by date_refresh desc limit ${(page - 1) * take},${take}`,
  ];
  return await fetch(uri.join(""));
}

export default function List() {
  const rowCount = 100;
  const history = useHistory();
  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const initialCity = urlParams.get("city") || "";
  const initialCategory = urlParams.get("category") || "";
  const initialIndustry = urlParams.get("industry") || "";
  const initialEducation = urlParams.get("education") || "";
  const initialSalary = urlParams.get("salary") || "";

  const updateUrlParams = (params) => {
    const searchParams = new URLSearchParams(location.search);
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value);
      } else {
        searchParams.delete(key);
      }
    });
    const newSearch = searchParams.toString();
    history.replace({
      pathname: location.pathname,
      search: newSearch ? `?${newSearch}` : "",
    });
  };

  const [state, dispatch] = useReducer(reducer, {
    BANNER: [],
    JOB: [],
    PAGE: 1,
    CITY: initialCity,
    CATEGORY: initialCategory,
    INDUSTRY: initialIndustry,
    EDUCATION: initialEducation,
    SALARY: initialSalary,
  });

  const [flag, setFlag] = useState(true);
  const [resetTrigger, setResetTrigger] = useState(0);
  const restoringFromCache = useRef(false);

  // 初始化 stateRef
  const stateRef = useRef(state);
  // Sync stateRef with state
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useScrollRestoration({
    stateRef,
    setStates: (parsed) => {
      // 检查缓存状态是否与当前 URL 参数匹配
      // 如果 URL 参数有变化（如用户手动清空），则不恢复旧缓存
      if (
        (parsed.CITY || "") !== initialCity ||
        (parsed.CATEGORY || "") !== initialCategory ||
        (parsed.INDUSTRY || "") !== initialIndustry ||
        (parsed.EDUCATION || "") !== initialEducation ||
        (parsed.SALARY || "") !== initialSalary
      ) {
        return;
      }

      // 如果缓存的 JOB 数据为空，则不恢复（避免恢复空数据）
      if (!parsed.JOB || parsed.JOB.length === 0) {
        return;
      }

      restoringFromCache.current = true;
      unstable_batchedUpdates(() => {
        if (parsed.CITY) dispatch({ type: "set", payload: { key: "CITY", value: parsed.CITY } });
        if (parsed.CATEGORY)
          dispatch({ type: "set", payload: { key: "CATEGORY", value: parsed.CATEGORY } });
        if (parsed.INDUSTRY)
          dispatch({ type: "set", payload: { key: "INDUSTRY", value: parsed.INDUSTRY } });
        if (parsed.EDUCATION)
          dispatch({ type: "set", payload: { key: "EDUCATION", value: parsed.EDUCATION } });
        if (parsed.SALARY)
          dispatch({ type: "set", payload: { key: "SALARY", value: parsed.SALARY } });
        if (parsed.BANNER && Array.isArray(parsed.BANNER)) {
          dispatch({ type: "set", payload: { key: "BANNER", value: parsed.BANNER } });
        }
        if (parsed.PAGE) dispatch({ type: "set", payload: { key: "PAGE", value: parsed.PAGE } });
        if (parsed.JOB && Array.isArray(parsed.JOB)) {
          dispatch({ type: "set", payload: { key: "JOB", value: parsed.JOB } });
        }
      });
      setTimeout(() => {
        restoringFromCache.current = false;
      }, 600);
    },
    storageKey: STORAGE_KEY,
  });

  const handleClick = (el) => {
    const nextCategory = state.CATEGORY === el ? "" : el;
    dispatch({ type: "set", payload: { key: "CATEGORY", value: nextCategory } });
    sessionStorage.removeItem(STORAGE_KEY);
    updateUrlParams({ category: nextCategory });
  };

  const handleChange = (val) => {
    dispatch({ type: "set", payload: { key: "CITY", value: val } });
    sessionStorage.removeItem(STORAGE_KEY);
    updateUrlParams({ city: val });
  };

  const loadNextPage = () => {
    dispatch({ type: "set", payload: { key: "PAGE", value: state.PAGE + 1 } });
  };

  useEffect(() => {
    // 只有在非恢复模式下且 BANNER 为空时才加载 Banner
    if (!restoringFromCache.current && 0 === state.BANNER.length) {
      fetch("/api/bulletin?option=banner-by-category&category=小程序-岗位")
        .then((response) => response.json())
        .then((data) => {
          dispatch({ type: "set", payload: { key: "BANNER", value: data } });
        });
    }
  }, []);

  React.useEffect(() => {
    if (restoringFromCache.current) return;

    const fetchData = async () => {
      const response = await getJobList({
        city: state.CITY,
        industry: state.INDUSTRY,
        education: state.EDUCATION,
        salary: state.SALARY,
        category: state.CATEGORY,
        page: 1,
      });
      if (response.status < 400) {
        const data = await response.json();
        dispatch({ type: "set", payload: { key: "JOB", value: data } });
        if (0 === data.length) {
          const loadEl = document.getElementById("load");
          if (loadEl) loadEl.innerHTML = "已经到底了";
        }
      }
    };

    // 请求第一页数据
    fetchData();
  }, [state.CITY, state.CATEGORY, state.INDUSTRY, state.EDUCATION, state.SALARY, resetTrigger]);

  React.useEffect(() => {
    // 如果是从缓存恢复期间，绝对不要执行基于页码变化的数据拉取
    // 因为缓存的数据已经包含了所有页面的数据，再次请求会导致数据重复或覆盖
    if (restoringFromCache.current) return;

    const fetchData = async () => {
      const response = await getJobList({
        city: state.CITY,
        industry: state.INDUSTRY,
        education: state.EDUCATION,
        salary: state.SALARY,
        category: state.CATEGORY,
        page: state.PAGE,
      });
      if (response.status < 400) {
        const data = await response.json();
        // 正常追加数据逻辑
        const newList = state.JOB.concat(data);
        dispatch({ type: "set", payload: { key: "JOB", value: newList } });

        if (0 === data.length) {
          const loadEl = document.getElementById("load");
          if (loadEl) loadEl.innerHTML = "已经到底了";
        }
      }
    };
    fetchData();
  }, [state.PAGE]);

  return (
    <>
      <div className="container-fluid">
        {0 < state.BANNER.length && <PlayImg category="小程序-岗位" list={state.BANNER} />}
        <div className="row pb-2 pt-2" style={{ backgroundColor: "#f5f5f5" }}>
          <div className="col">
            <input
              type="text"
              className="w-100 border-0 text-center rounded-pill"
              placeholder="按照企业/职位名称查询"
              onClick={() => {
                window.location.href = "#岗位/查询/";
              }}
              style={{ outline: 0, height: 35 }}
            />
          </div>
        </div>
        <div className="card border-0 mt-1 shadow">
          <div className="card-body">
            <div className="row mb-3" style={{ fontSize: 14 }}>
              <div className="col">
                <CityDropdowns defaultValue={initialCity} handleChange={handleChange} />
              </div>
              <div className="col flex-end">
                <div className="pull-right text-muted">
                  <span
                    className={
                      state.CATEGORY === "兼职" || state.CATEGORY === ""
                        ? "text-success bg-light"
                        : "text-muted"
                    }
                    onClick={() => handleClick("兼职")}
                  >
                    兼职
                  </span>
                  &nbsp;&nbsp;|&nbsp;&nbsp;
                  <span
                    className={
                      state.CATEGORY === "全职" || state.CATEGORY === ""
                        ? "text-success bg-light"
                        : "text-muted"
                    }
                    onClick={() => handleClick("全职")}
                  >
                    全职
                  </span>
                  &nbsp;&nbsp;|&nbsp;&nbsp;
                  <span
                    className={
                      state.CATEGORY === "实习" || state.CATEGORY === ""
                        ? "text-success bg-light"
                        : "text-muted"
                    }
                    onClick={() => handleClick("实习")}
                  >
                    实习
                  </span>
                  &nbsp;&nbsp;|&nbsp;&nbsp;
                  <span
                    className={state.CATEGORY === "" ? "text-success bg-light" : "text-muted"}
                    onClick={() => handleClick("")}
                  >
                    全选
                  </span>
                </div>
              </div>
            </div>
            <div className="mb-3 px-4 d-flex flex-nowrap">
              <div className="flex-grow-1 px-1" style={{ minWidth: 0 }}>
                <IndustryPicker
                  value={initialIndustry}
                  onChange={(e) => {
                    dispatch({ type: "set", payload: { key: "INDUSTRY", value: e.target.value } });
                    sessionStorage.removeItem(STORAGE_KEY);
                    updateUrlParams({ industry: e.target.value });
                  }}
                />
              </div>
              <div className="flex-grow-1 px-1" style={{ minWidth: 0 }}>
                <EducationPicker
                  value={initialEducation}
                  onChange={(e) => {
                    dispatch({ type: "set", payload: { key: "EDUCATION", value: e.target.value } });
                    sessionStorage.removeItem(STORAGE_KEY);
                    updateUrlParams({ education: e.target.value });
                  }}
                />
              </div>
              <div className="flex-grow-1 px-1" style={{ minWidth: 0 }}>
                <Salary
                  value={initialSalary}
                  onChange={(e) => {
                    dispatch({ type: "set", payload: { key: "SALARY", value: e.target.value } });
                    sessionStorage.removeItem(STORAGE_KEY);
                    updateUrlParams({ salary: e.target.value });
                  }}
                />
              </div>
            </div>
            <div>
              {0 < state.JOB.length &&
                state.JOB.map((item) => (
                  <div key={item.id}>
                    <RecruitmentRow1
                      {...item}
                      addressLevel1={item.address1}
                      addressLevel2={item.address2}
                      refreshTime={item.date_refresh}
                      id={Number(item.id)}
                      uuid={item.uuid}
                      fairId={"0"}
                    />
                    <hr style={{ marginTop: "0.5rem", marginBottom: "0.5rem" }} />
                  </div>
                ))}
            </div>
            {flag === true ? (
              <button id="load" className="btn btn-block mx-auto" onClick={loadNextPage}>
                点击加载更多
              </button>
            ) : (
              <div className="d-flex justify-content-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* 浮动重置按钮 */}
      <button
        className="btn btn-outline-secondary rounded-circle position-fixed"
        style={{
          bottom: "70px",
          left: "20px",
          width: "45px",
          height: "45px",
          zIndex: 1000,
          padding: 0,
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}
        onClick={() => {
          // 清除缓存
          sessionStorage.removeItem(STORAGE_KEY);
          sessionStorage.removeItem("industry");

          // 直接重置组件状态并滚动到顶部
          dispatch({ type: "set", payload: { key: "JOB", value: [] } });
          dispatch({ type: "set", payload: { key: "PAGE", value: 1 } });
          dispatch({ type: "set", payload: { key: "CITY", value: "" } });
          dispatch({ type: "set", payload: { key: "CATEGORY", value: "" } });
          dispatch({ type: "set", payload: { key: "INDUSTRY", value: "" } });
          dispatch({ type: "set", payload: { key: "EDUCATION", value: "" } });
          dispatch({ type: "set", payload: { key: "SALARY", value: "" } });

          // 关闭缓存恢复标记，确保请求不会被阻止
          restoringFromCache.current = false;

          // 触发 useEffect 重新请求数据
          setResetTrigger((prev) => prev + 1);

          // 更新 URL 参数
          updateUrlParams({ city: "", category: "", industry: "", education: "", salary: "" });

          // 滚动到顶部
          window.scrollTo({ top: 0, behavior: "instant" });
        }}
        title="重置筛选"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
          <path d="M3 3v5h5" />
        </svg>
      </button>
      <Navbar category="岗位" />
    </>
  );
}
